import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../../auth/AuthContext'
import {
  detectPlatform,
  installGuide,
  installRequiredForPush,
  isStandalone,
  notificationPermission,
  supportsPush,
  type InstallPromptEvent,
} from '../../lib/platform'
import { currentPushEndpoint, enablePush, showTestNotification } from '../../lib/push'
import { Notice, PageHeader, Section } from '../../components/ui'
import { vapidPublicKey } from '../../lib/supabase'

/**
 * Setup: install, then notifications.
 *
 * This is not a settings page tucked away in a menu. On iPhone, web push only
 * works for a PWA opened from the home screen, and the permission prompt must
 * follow a user gesture. If a coach skips this, the entire reminder engine
 * silently does not reach them and Michel is back to chasing on WhatsApp. So
 * the app nags until both steps are done, and says plainly why.
 */
const PERMISSION_LABEL: Record<NotificationPermission | 'unsupported', string> = {
  default: 'Not asked yet',
  granted: 'Allowed',
  denied: 'Blocked',
  unsupported: 'Not supported here',
}

export function OnboardingPage() {
  const { profile } = useAuth()
  const platform = detectPlatform()
  const guide = installGuide(platform)

  const [installed, setInstalled] = useState(isStandalone)
  const [permission, setPermission] = useState(notificationPermission)
  const [endpoint, setEndpoint] = useState<string | null>(null)
  const [installPrompt, setInstallPrompt] = useState<InstallPromptEvent | null>(null)
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState<{ tone: 'info' | 'warning' | 'success'; text: string } | null>(
    null,
  )

  const refresh = useCallback(() => {
    setInstalled(isStandalone())
    setPermission(notificationPermission())
    void currentPushEndpoint().then(setEndpoint)
  }, [])

  // installed / permission are read straight into state above, so mount only
  // needs the asynchronous one. Coming back from the share sheet or the iOS
  // settings app re-reads everything.
  useEffect(() => {
    void currentPushEndpoint().then(setEndpoint)
    const onVisible = () => {
      if (document.visibilityState === 'visible') refresh()
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [refresh])

  useEffect(() => {
    function onBeforeInstallPrompt(event: Event) {
      event.preventDefault()
      setInstallPrompt(event as InstallPromptEvent)
    }
    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt)
    window.addEventListener('appinstalled', refresh)
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt)
      window.removeEventListener('appinstalled', refresh)
    }
  }, [refresh])

  const pushBlocked = installRequiredForPush(platform) && !installed
  const notificationsOn = permission === 'granted' && Boolean(endpoint)
  const allDone = installed && notificationsOn

  async function onInstall() {
    if (!installPrompt) return
    await installPrompt.prompt()
    const choice = await installPrompt.userChoice
    setInstallPrompt(null)
    if (choice.outcome === 'accepted') {
      setMessage({
        tone: 'success',
        text: 'Installed. Open La Folie from your home screen to finish the next step.',
      })
    }
    refresh()
  }

  /** Must stay a direct click handler. Browsers reject a permission prompt that is not a gesture. */
  async function onEnableNotifications() {
    if (!profile) return
    setBusy(true)
    setMessage(null)
    const result = await enablePush(profile.id)
    setBusy(false)
    refresh()
    if (result.ok) {
      setMessage({ tone: 'success', text: 'Notifications are on for this device.' })
    } else {
      setMessage({ tone: 'warning', text: result.reason })
    }
  }

  return (
    <>
      <PageHeader
        title="Set up notifications"
        subtitle="Two steps, about a minute. This is how your event reminders reach you."
      />

      {allDone ? (
        <div style={{ marginBottom: 'var(--sp-5)' }}>
          <Notice tone="success" title="You are all set">
            You will get a reminder 48 hours and 24 hours before every event you are assigned to.
            <div style={{ marginTop: 'var(--sp-3)' }}>
              <Link className="btn btn--sm" to="/">
                Back to my events
              </Link>
            </div>
          </Notice>
        </div>
      ) : null}

      {message ? (
        <div style={{ marginBottom: 'var(--sp-5)' }}>
          <Notice tone={message.tone}>{message.text}</Notice>
        </div>
      ) : null}

      <Section title="Steps">
        <div className="steps">
          <Step
            number={1}
            title={guide.heading}
            state={installed ? 'done' : 'active'}
            body={
              installed
                ? 'Done. You are running the installed app.'
                : 'Adding the app to your home screen is what makes reminders possible.'
            }
          >
            {!installed ? (
              <>
                <ol className="step__list">
                  {guide.steps.map((step) => (
                    <li key={step}>{step}</li>
                  ))}
                </ol>
                {guide.note ? (
                  <p className="faint" style={{ marginTop: 'var(--sp-2)' }}>
                    {guide.note}
                  </p>
                ) : null}
                {installPrompt ? (
                  <div className="step__actions">
                    <button type="button" className="btn" onClick={() => void onInstall()}>
                      Install the app
                    </button>
                  </div>
                ) : null}
                <div className="step__actions">
                  <button type="button" className="btn btn--secondary btn--sm" onClick={refresh}>
                    I have done this
                  </button>
                </div>
              </>
            ) : null}
          </Step>

          <Step
            number={2}
            title="Turn on notifications"
            state={notificationsOn ? 'done' : pushBlocked ? 'locked' : 'active'}
            body={
              notificationsOn
                ? 'Done. This device is registered for reminders.'
                : pushBlocked
                  ? 'Available after step 1. On iPhone, notifications only work from the installed app.'
                  : 'Your phone will ask for permission. Choose Allow.'
            }
          >
            {!notificationsOn ? (
              <>
                {!supportsPush() ? (
                  <p className="faint">
                    This browser cannot receive notifications. On iPhone use Safari, and open the
                    app from your home screen.
                  </p>
                ) : null}
                {!vapidPublicKey ? (
                  <p className="faint">
                    Push keys are not configured in this build yet, so this button will report that.
                    Reminders still appear in your inbox.
                  </p>
                ) : null}
                {permission === 'denied' ? (
                  <p className="faint">
                    Notifications are blocked. On iPhone: Settings, Notifications, La Folie. On
                    Android: long press the app icon, App info, Notifications.
                  </p>
                ) : null}
                <div className="step__actions">
                  <button
                    type="button"
                    className="btn"
                    disabled={busy || pushBlocked || !supportsPush()}
                    onClick={() => void onEnableNotifications()}
                  >
                    {busy ? 'Enabling' : 'Turn on notifications'}
                  </button>
                </div>
              </>
            ) : (
              <div className="step__actions">
                <button
                  type="button"
                  className="btn btn--secondary btn--sm"
                  onClick={() => void showTestNotification()}
                >
                  Send me a test notification
                </button>
              </div>
            )}
          </Step>
        </div>
      </Section>

      <Section title="If a reminder is missed">
        <Notice tone="info">
          Every reminder also lands in your in-app inbox, so nothing is lost if a notification does
          not arrive. Opening it marks it as seen, which tells Michel he does not need to chase you
          on WhatsApp.
          <div style={{ marginTop: 'var(--sp-3)' }}>
            <Link className="btn btn--sm btn--secondary" to="/inbox">
              Open my inbox
            </Link>
          </div>
        </Notice>
      </Section>

      <Section title="This device">
        <div className="card card--muted">
          <dl className="deflist">
            <div>
              <dt>Platform</dt>
              <dd>{platform === 'ios' ? 'iPhone or iPad' : platform === 'android' ? 'Android' : 'Desktop'}</dd>
            </div>
            <div>
              <dt>Installed</dt>
              <dd>{installed ? 'Yes' : 'No'}</dd>
            </div>
            <div>
              <dt>Notification permission</dt>
              <dd>{PERMISSION_LABEL[permission]}</dd>
            </div>
            <div>
              <dt>Push registered</dt>
              <dd>{endpoint ? 'Yes' : 'No'}</dd>
            </div>
          </dl>
        </div>
      </Section>
    </>
  )
}

function Step({
  number,
  title,
  body,
  state,
  children,
}: {
  number: number
  title: string
  body: string
  state: 'done' | 'active' | 'locked'
  children?: ReactNode
}) {
  return (
    <div className={`step step--${state}`}>
      <span className="step__num">{state === 'done' ? '+' : number}</span>
      <div>
        <p className="step__title">{title}</p>
        <p className="step__body">{body}</p>
        {children}
      </div>
    </div>
  )
}
